import type { TFunction } from "i18next";

import { Icon, Typo } from "@pepperstone/pepper-ui-kit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LeadV2Status } from "../api/generated";
import { ApplicationStartedIcon } from "../Icons";
import { MobileStatusModal } from "../LeadPage/LeadsTable/MobileStatusModal";
import { LeadRawStatus } from "../Overview/RecentLeadStatus/types";
import { TooltipWrapper } from "../Tooltip";

const getNewLeadStatusStyle = (status: LeadV2Status) => {
  switch (status) {
    // New Lead Statuses styling
    case LeadV2Status.IncompleteProfile:
      return {
        icon: <Icon icon="clock" size="small" />,
        bgColor: "bg-surface-tags-grey",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-base",
      };
    case LeadV2Status.CompletingProfile:
      return {
        icon: (
          <ApplicationStartedIcon data-testid="application-started-icon-completing-profile" />
        ),
        bgColor: "bg-surface-tags-blue",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-blue",
      };
    case LeadV2Status.ReviewingSuitability:
      return {
        icon: (
          <ApplicationStartedIcon data-testid="application-started-icon-reviewing-suitability" />
        ),
        bgColor: "bg-surface-tags-blue",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-blue",
      };
    case LeadV2Status.IdentifyVerification:
      return {
        icon: (
          <ApplicationStartedIcon data-testid="application-started-icon-identify-verification" />
        ),
        bgColor: "bg-surface-tags-blue",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-blue",
      };
    case LeadV2Status.Approved:
      return {
        icon: <Icon icon="clock" size="small" />,
        bgColor: "bg-surface-tags-amber",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-warning-bold",
      };
    case LeadV2Status.VerifyingIdentity:
      return {
        icon: <Icon icon="clock" size="small" />,
        bgColor: "bg-surface-accent-amber-subtler",
        hoverOpacity: "hover:opacity-5",
        txtColor: "text-text-warning-bold",
      };
    default:
      return {
        icon: <Icon icon="slash-circle" size="small" />,
        bgColor: "bg-surface-disabled",
        txtColor: "text-text-tertiary",
      };
  }
};

export const LeadStatusDisplay = ({
  id,
  status,
  displayValue,
  isMobile,
  toolTipDescription = "",
  showTooltip = true,
  onHover,
}: {
  id?: string;
  status: LeadV2Status;
  displayValue: string;
  isMobile?: boolean;
  toolTipDescription?: string;
  showTooltip?: boolean;
  onHover?: (status: LeadV2Status, isMobile: boolean) => void;
}) => {
  const [mobileModalStatusVisible, setMobileModalStatusVisible] =
    useState(false);

  const statusStyle = getNewLeadStatusStyle(status);
  const toolTipText = useStatusDescriptionMapping(status) || toolTipDescription;
  return !isMobile ? (
    <div
      data-testid="status-display-value-desktop-wrapper"
      className="flex flex-row items-center gap-2"
    >
      <TooltipWrapper
        id={`tooltip-${id}`}
        color="blue"
        tooltipPlacement="top"
        text={showTooltip ? toolTipText : ""}
      >
        <div
          className={`${statusStyle.bgColor} ${statusStyle.txtColor} flex flex-shrink-0 flex-row items-center justify-start rounded-md px-2 py-1 hover:opacity-100`}
          onMouseEnter={() => onHover?.(status, false)}
        >
          <div className="pe-1">{statusStyle.icon}</div>
          <Typo elem="p" size={isMobile ? "sm" : "md"} type="label">
            {displayValue}
          </Typo>
        </div>
      </TooltipWrapper>
    </div>
  ) : (
    <div
      data-testid="status-display-value-mobile-wrapper"
      className="flex flex-row items-center justify-end gap-1"
    >
      <MobileStatusModal
        isVisible={mobileModalStatusVisible}
        close={() => setMobileModalStatusVisible(false)}
        status={displayValue}
        statusDescription={toolTipText}
      />
      <div
        className={`${statusStyle.bgColor} ${statusStyle.txtColor} flex flex-shrink-0 flex-row items-center justify-start rounded-md px-2 py-1 hover:opacity-100`}
      >
        <div className="pe-1">{statusStyle.icon}</div>
        <Typo elem="p" size={isMobile ? "sm" : "md"} type="label">
          {displayValue}
        </Typo>
      </div>
      <div
        onClick={() => {
          setMobileModalStatusVisible(true);
          onHover?.(status, true);
        }}
      >
        <div className="text-icon-tertiary">
          <Icon icon="help" size="small" />
        </div>
      </div>
    </div>
  );
};

export const useStatusDescriptionMapping = (status: LeadV2Status): string => {
  const { t } = useTranslation();
  const translatedDescriptions = new Map<string, string>([
    [
      LeadV2Status.IncompleteProfile,
      t("leadsPage_table_status_incomplete_profile_description"),
    ],
    [
      LeadV2Status.CompletingProfile,
      t("leadsPage_table_status_completing_profile_description"),
    ],
    [
      LeadV2Status.ReviewingSuitability,
      t("leadsPage_table_status_review_suitability_description"),
    ],
    [
      LeadV2Status.IdentifyVerification,
      t("leadsPage_table_status_identify_verification_description"),
    ],
    [LeadV2Status.Approved, t("leadsPage_table_status_approved_description")],
    [
      LeadV2Status.VerifyingIdentity,
      t("leadsPage_table_status_verifying_identity_description"),
    ],
    [LeadV2Status.Unknown, t("leadsPage_table_status_unknown_description")],
  ]);
  return translatedDescriptions.get(status) ?? "";
};

export const buildStatusMapping = (t: TFunction<"translation", undefined>) =>
  new Map<string, string>([
    // LeadV2Status
    [LeadV2Status.Unknown, t("leadsPage_table_status_unknown")],
    [
      LeadV2Status.IncompleteProfile,
      t("leadsPage_table_status_incomplete_profile"),
    ],
    [
      LeadV2Status.CompletingProfile,
      t("leadsPage_table_status_completing_profile"),
    ],
    [
      LeadV2Status.ReviewingSuitability,
      t("leadsPage_table_status_review_suitability"),
    ],
    [
      LeadV2Status.IdentifyVerification,
      t("leadsPage_table_status_identify_verification"),
    ],
    [LeadV2Status.Approved, t("leadsPage_table_status_approved")],
    [
      LeadV2Status.VerifyingIdentity,
      t("leadsPage_table_status_verifying_identity"),
    ],
    // LeadRawStatus
    [
      LeadRawStatus.Demo,
      t("leadsPage_table_status_demo_label", {
        defaultValue: "Live account setup",
      }),
    ],
    [
      LeadRawStatus.Lead_1,
      t("leadsPage_table_status_lead_1_label", { defaultValue: "E&I" }),
    ],
    [
      LeadRawStatus.Lead_2,
      t("leadsPage_table_status_lead_2_label", {
        defaultValue: "Experience check",
      }),
    ],
    [
      LeadRawStatus.Lead_4,
      t("leadsPage_table_status_lead_4_label", {
        defaultValue: "Identity verification",
      }),
    ],
    [
      LeadRawStatus.Lead_5,
      t("leadsPage_table_status_lead_5_label", {
        defaultValue: "Under review",
      }),
    ],
    [
      LeadRawStatus.Approved,
      t("leadsPage_table_status_approved", { defaultValue: "Approved" }),
    ],
    [
      LeadRawStatus.Unknown,
      t("leadsPage_table_status_unknown", { defaultValue: "Unknown" }),
    ],
  ]);
